import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/api';
import { FiCheckCircle, FiX, FiUploadCloud, FiUserPlus, FiLoader } from 'react-icons/fi';
import styles from './ManualUploadModal.module.css';

const ManualUploadModal = ({ onClose, onSuccess, userEmail, apiConfig }) => {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    
    const [manualForm, setManualForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        jobTitle: '',
        file: null,
        importBoond: apiConfig.boond,
        importHubspot: apiConfig.hubspot,
        importMyHub: apiConfig.myhub
    });

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setManualForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setManualForm(prev => ({ ...prev, file: e.target.files[0] }));
        }
    };

    const handleDropzoneClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!manualForm.file || !manualForm.lastName || !manualForm.email) {
            alert("Merci de remplir au moins le Nom, l'Email et d'ajouter un CV.");
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('cv_file', manualForm.file);
        formData.append('first_name', manualForm.firstName);
        formData.append('last_name', manualForm.lastName);
        formData.append('email', manualForm.email);
        formData.append('phone', manualForm.phone);
        formData.append('job_title', manualForm.jobTitle);
        formData.append('import_boondmanager', manualForm.importBoond);
        formData.append('import_hubspot', manualForm.importHubspot);
        formData.append('import_myhubconsulting', manualForm.importMyHub);
        formData.append('language', 'FR');
        formData.append('user_email', userEmail);

        try {
            await api.post('/manual-process', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            // Alerte native gardée (peut être remplacée par un toast)
            alert("Succès ! Candidat envoyé aux CRM (Service Gratuit).");
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Erreur manuel:", error);
            alert(error.response?.data?.error || "Une erreur est survenue.");
        } finally {
            setUploading(false);
        }
    };

    // Utilisation du Portal pour sécuriser l'affichage
    return createPortal(
        <div className={styles.overlay}>
            <div className={styles.content}>
                
                {/* ── En-tête ── */}
                <div className={styles.header}>
                    <div className={styles.headerTitleGroup}>
                        <div className={styles.headerIconBadge}>
                            <FiUserPlus size={18} />
                        </div>
                        <div>
                            <h3 className={styles.headerTitle}>Ajout Manuel</h3>
                            <p className={styles.headerSubtitle}>Envoi direct vers vos CRM connectés</p>
                        </div>
                    </div>
                    <button className={styles.closeBtn} onClick={onClose} title="Fermer">
                        <FiX size={20} />
                    </button>
                </div>

                {/* ── Message d'information ── */}
                <div className={styles.alertInfo}>
                    <FiCheckCircle size={18} className={styles.alertIcon} />
                    <div>
                        Mode <strong>GRATUIT</strong> : Envoi direct aux CRM sans analyse IA ni génération de dossier PPTX.
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    
                    {/* ── Grille Informations Personnelles ── */}
                    <div className={styles.grid}>
                        <div className={styles.formGroup}>
                            <label>Prénom</label>
                            <input 
                                type="text" name="firstName" className={styles.input} 
                                value={manualForm.firstName} onChange={handleInputChange} 
                                placeholder="Jean"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Nom *</label>
                            <input 
                                type="text" name="lastName" className={styles.input} required 
                                value={manualForm.lastName} onChange={handleInputChange} 
                                placeholder="Dupont"
                            />
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label>Email *</label>
                        <input 
                            type="email" name="email" className={styles.input} required 
                            value={manualForm.email} onChange={handleInputChange} 
                            placeholder="jean.dupont@email.com"
                        />
                    </div>

                    <div className={styles.grid}>
                        <div className={styles.formGroup}>
                            <label>Téléphone</label>
                            <input 
                                type="text" name="phone" className={styles.input} 
                                value={manualForm.phone} onChange={handleInputChange} 
                                placeholder="06 12 34 56 78"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Intitulé du poste</label>
                            <input 
                                type="text" name="jobTitle" className={styles.input} 
                                value={manualForm.jobTitle} onChange={handleInputChange} 
                                placeholder="Ex: Développeur Fullstack" 
                            />
                        </div>
                    </div>

                    {/* ── Destinations CRM ── */}
                    <div className={styles.formGroup}>
                        <label>Destinations (CRM)</label>
                        <div className={styles.destinationsList}>
                            
                            <label className={`${styles.destinationRow} ${!apiConfig.boond ? styles.destinationDisabled : ''}`}>
                                <div className={styles.destinationInfo}>
                                    <span className={styles.destinationAvatar}>BM</span>
                                    <span className={styles.destinationName}>BoondManager</span>
                                </div>
                                <div className={styles.toggleWrap}>
                                    <input 
                                        type="checkbox" 
                                        name="importBoond" 
                                        checked={manualForm.importBoond} 
                                        onChange={handleInputChange} 
                                        disabled={!apiConfig.boond}
                                        className={styles.toggleInput}
                                    />
                                    <span className={styles.toggleTrack}></span>
                                </div>
                            </label>

                            <label className={`${styles.destinationRow} ${!apiConfig.hubspot ? styles.destinationDisabled : ''}`}>
                                <div className={styles.destinationInfo}>
                                    <span className={styles.destinationAvatar}>HS</span>
                                    <span className={styles.destinationName}>HubSpot</span>
                                </div>
                                <div className={styles.toggleWrap}>
                                    <input 
                                        type="checkbox" 
                                        name="importHubspot" 
                                        checked={manualForm.importHubspot} 
                                        onChange={handleInputChange} 
                                        disabled={!apiConfig.hubspot}
                                        className={styles.toggleInput}
                                    />
                                    <span className={styles.toggleTrack}></span>
                                </div>
                            </label>

                            <label className={`${styles.destinationRow} ${!apiConfig.myhub ? styles.destinationDisabled : ''}`}>
                                <div className={styles.destinationInfo}>
                                    <span className={styles.destinationAvatar}>MH</span>
                                    <span className={styles.destinationName}>MyHubConsulting</span>
                                </div>
                                <div className={styles.toggleWrap}>
                                    <input 
                                        type="checkbox" 
                                        name="importMyHub" 
                                        checked={manualForm.importMyHub} 
                                        onChange={handleInputChange} 
                                        disabled={!apiConfig.myhub}
                                        className={styles.toggleInput}
                                    />
                                    <span className={styles.toggleTrack}></span>
                                </div>
                            </label>

                        </div>
                    </div>

                    {/* ── Fichier CV (Dropzone stylisée) ── */}
                    <div className={styles.formGroup}>
                        <label>Fichier CV original *</label>
                        <div className={styles.fileDropzone} onClick={handleDropzoneClick}>
                            <input 
                                type="file" 
                                className={styles.fileInputHidden} 
                                required 
                                accept=".pdf,.docx,.doc" 
                                onChange={handleFileChange}
                                ref={fileInputRef} 
                            />
                            <div className={styles.fileDropzoneIcon}>
                                <FiUploadCloud size={18} />
                            </div>
                            <span className={styles.fileDropzoneText}>
                                {manualForm.file ? manualForm.file.name : "Cliquez pour sélectionner un fichier..."}
                            </span>
                            <span className={styles.fileDropzoneHint}>PDF / DOCX</span>
                        </div>
                    </div>

                    {/* ── Actions ── */}
                    <div className={styles.actions}>
                        <button type="button" className={styles.btnCancel} onClick={onClose} disabled={uploading}>
                            Annuler
                        </button>
                        <button type="submit" className={styles.btnSubmit} disabled={uploading}>
                            {uploading ? (
                                <>
                                    <FiLoader size={16} className={styles.spinIcon} />
                                    Traitement...
                                </>
                            ) : (
                                'Valider et Envoyer'
                            )}
                        </button>
                    </div>

                </form>
            </div>
        </div>,
        document.body
    );
};

export default ManualUploadModal;